import React, { useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { selectUser } from "../../../../store/slices/authentication";

const FIELD_LABELS = {
  email: "Email",
  phone_number: "Phone",
  state_of_residency: "Location",
  job_or_passion: "Job / Passion",
  description: "About",
};

// How each field's value is drawn: email and phone as links a tap can use,
// About with the member's own line breaks. Everything wraps, so a long email
// or web address can't push the card off a phone screen.
function FieldValue({ field, value }) {
  const text = "text-[16px] font-body text-dupontBlue dark:text-darkText break-words [overflow-wrap:anywhere] min-w-0";
  if (field === "email") {
    return (
      <a href={`mailto:${value}`} className={`${text} block hover:underline`}>
        {value}
      </a>
    );
  }
  if (field === "phone_number") {
    const dialable = String(value).replace(/[^\d+]/g, "");
    return dialable ? (
      <a href={`tel:${dialable}`} className={`${text} block hover:underline`}>
        {value}
      </a>
    ) : (
      <div className={text}>{value}</div>
    );
  }
  if (field === "description") {
    return <div className={`${text} whitespace-pre-wrap`}>{value}</div>;
  }
  return <div className={text}>{value}</div>;
}

const FIELD_KEYS = [
  "state_of_residency",
  "email",
  "phone_number",
  "job_or_passion",
  "description",
];

function ProfileCard({ profile, individualId }) {
  const navigate = useNavigate();
  const user = useSelector(selectUser);
  // The server refuses a letter to yourself, so the member's own card doesn't
  // offer one.
  const isMe = user?.externalId != null && String(individualId) === String(user.externalId);
  const visibleFields = FIELD_KEYS.filter((key) => profile[key]);
  const hiddenFields = FIELD_KEYS.filter((key) => !profile[key]);
  const [showPhoto, setShowPhoto] = useState(false);
  // A photo that fails to load takes its button with it (an invisible button
  // would still catch the keyboard), and the initial shows instead.
  const [failedPhoto, setFailedPhoto] = useState(null);
  const hasPhoto = profile.profile_picture_url && failedPhoto !== profile.profile_picture_url;

  return (
    <div className="flex flex-col rounded-[12px] shadow-sm p-[24px] bg-white dark:bg-darkCard border border-dupontCreamDk dark:border-darkGold/10 gap-y-[12px]">
      {/* Profile Close Up overlay — rendered via portal so it sits above everything */}
      {showPhoto && (profile.closeup_picture_url || profile.profile_picture_url) && createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/80 p-[24px]"
          onClick={() => setShowPhoto(false)}
        >
          <div className="flex flex-col items-center gap-y-[16px] max-w-[500px]" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-[22px] font-display font-bold text-white drop-shadow-lg">
              {profile.preferred_name || profile.name?.split(" ")[0] || "Profile"}
            </h3>
            <img
              src={profile.closeup_picture_url || profile.profile_picture_url}
              alt={profile.name}
              className="max-w-[85vw] max-h-[70vh] rounded-[16px] shadow-2xl object-contain border-2 border-dupontGold"
            />
            <button
              onClick={() => setShowPhoto(false)}
              className="px-[24px] py-[10px] rounded-full bg-dupontGold text-white text-[15px] font-display font-semibold hover:bg-dupontGold/90 transition-colors shadow-lg"
            >
              Close
            </button>
          </div>
        </div>,
        document.body
      )}

      {/* Picture + Name */}
      <div className="flex items-center gap-x-[12px]">
        {hasPhoto ? (
          <button
            type="button"
            onClick={() => setShowPhoto(true)}
            aria-label={`See a larger photo of ${profile.name || "this family member"}`}
            className="flex-shrink-0 p-0 m-0 bg-transparent border-0 rounded-full cursor-pointer"
          >
            <img
              src={profile.profile_picture_url}
              alt={profile.name}
              loading="lazy"
              decoding="async"
              className="block w-[48px] h-[48px] rounded-full object-cover border border-dupontGold hover:ring-2 hover:ring-dupontGold/50 transition-all duration-200"
              onError={() => setFailedPhoto(profile.profile_picture_url)}
            />
          </button>
        ) : (
          <div className="w-[48px] h-[48px] rounded-full bg-dupontBlue/10 dark:bg-darkGold/10 flex items-center justify-center text-dupontBlue dark:text-darkTextTertiary font-display font-bold text-[18px]">
            {profile.name?.charAt(0) || "?"}
          </div>
        )}
        <div className="min-w-0">
          <div className="text-[18px] font-display font-bold text-dupontBlue dark:text-darkText break-words">
            {profile.name}
          </div>
          {profile.preferred_name && (
            <div className="text-[19px] font-body text-dupontGold dark:text-darkGold italic mt-[2px] break-words">
              Goes by "{profile.preferred_name}"
            </div>
          )}
        </div>
      </div>

      {/* Visible fields */}
      {visibleFields.length > 0 ? (
        <div className="flex flex-col gap-y-[8px]">
          {visibleFields.map((key) => (
            <div key={key}>
              <div className="text-[12px] font-bold text-dupontBlue/50 dark:text-darkGold uppercase tracking-wide">
                {FIELD_LABELS[key]}
              </div>
              <FieldValue field={key} value={profile[key]} />
            </div>
          ))}
        </div>
      ) : (
        <div className="text-[15px] font-body text-dupontBlue/40 dark:text-darkTextTertiary italic">
          No shared fields visible yet
        </div>
      )}

      {/* Hint for hidden fields */}
      {hiddenFields.length > 0 && (
        <div className="border-t border-dupontCreamDk dark:border-darkGold/10 pt-[8px] mt-[4px]">
          <div className="text-[16px] font-body text-dupontGold/80 dark:text-darkGold italic">
            Toggle on more fields in Social Settings (on your My Profile tab) to see more here
          </div>
        </div>
      )}

      {/* Write a letter link */}
      {individualId && !isMe && (
        <div className="border-t border-dupontCreamDk dark:border-darkGold/10 pt-[12px] mt-[4px]">
          <button
            onClick={() => navigate(`/connect?tab=correspondence&letters=compose&to=${individualId}`)}
            className="text-[15px] font-display font-semibold text-dupontGold dark:text-darkGold hover:underline cursor-pointer transition-colors"
          >
            Write a letter to {profile.preferred_name || profile.name?.split(" ")[0] || "this family member"}
          </button>
        </div>
      )}
    </div>
  );
}

export default ProfileCard;
